import { createDraftToken } from '@/onboarding-start/api';
import {
  clearHandoffValue,
  readHandoffValue,
  writeHandoffValue
} from '@/onboarding-start/handoffStorage';

/**
 * Anonymous draft token — the funnel join key (S20).
 *
 * Minted on `/start` page load so every pre-auth event carries it, including the
 * page-view that is the denominator for the whole funnel. It rides into the
 * authenticated session on the vision handoff and is reported one last time when
 * the draft is claimed, which is what joins the pre-auth funnel to the post-signup
 * one.
 *
 * The token is issued by the server (`POST /api/onboarding/drafts`), and the same
 * value labels every event here, so the two halves of the funnel still share one
 * identifier. It is issued rather than minted here because it is also what tells the
 * server a product came through `/start`: a token the browser invented was a claim
 * anyone could make, and it carried a free journey with it.
 *
 * Stored through `handoffStorage`, which keeps it in this tab and in a copy that
 * survives one: the confirmation link opens a new tab, and a token that did not
 * make the trip means no claim and an empty PRD. Its lifetime matches the vision
 * handoff it accompanies, because neither is any use without the other.
 */
export const ONBOARDING_DRAFT_TOKEN_KEY = 'codalio-onboarding-draft-token';

function mint(): string {
  // Only for a visit the server could not issue a token to. It correlates this
  // visitor's own events and nothing else: the server does not know it, so no draft is
  // claimed and the product created after signup is an ordinary one. Analytics degrade,
  // the trust boundary holds.
  //
  // `crypto.randomUUID` needs a secure context; the fallback keeps a usable (if
  // lower-entropy) correlation id on a plain-http preview host.
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }

  return `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

let inFlight: Promise<string> | null = null;

/**
 * One token for the whole visit, asked for on first call so a visitor who leaves without
 * submitting is still counted, and reused across a reload in the same tab so they keep
 * the same draft row.
 *
 * The server issues it. A local one is used only when that call fails, and it correlates
 * this visitor's events without claiming anything — see `mint`.
 */
export async function ensureDraftToken(): Promise<string> {
  const existing = readHandoffValue(ONBOARDING_DRAFT_TOKEN_KEY);
  if (existing) return existing;

  // One request, however many callers ask while it is open. This is called on mount
  // and again on submit, so a visitor who types and presses Generate before the first
  // call answers used to start a second one: two drafts, the later token stored, and
  // the vision saved against the one the product would not claim.
  inFlight ??= (async () => {
    const token = (await createDraftToken()) ?? mint();
    // Storage disabled (Safari private mode) is swallowed inside the helper: events
    // still fire, they just cannot be joined across the reload — better than
    // dropping them entirely.
    writeHandoffValue(ONBOARDING_DRAFT_TOKEN_KEY, token);
    return token;
  })().finally(() => {
    inFlight = null;
  });

  return inFlight;
}

/** Read without minting: callers after signup must not create a fresh token. */
export function peekDraftToken(): string | null {
  return readHandoffValue(ONBOARDING_DRAFT_TOKEN_KEY);
}

/** Called once the draft is claimed, so a second product create cannot re-report it. */
export function clearDraftToken(): void {
  inFlight = null;
  clearHandoffValue(ONBOARDING_DRAFT_TOKEN_KEY);
}
